import React, { ReactNode, useState, useEffect } from 'react';
import { useLocation, Link } from 'wouter';
import { useQuery } from "@tanstack/react-query";
import { useMobile } from '@/hooks/use-mobile';
import { apiRequest } from '@/lib/queryClient';

// Interface for admin stats data
interface AdminStats {
  totalTenants?: number;
  activeTenants?: number;
  pendingTenants?: number;
  activeUsers?: number;
  activeLicenses?: number;
  mrr?: number;
  mrrChange?: number;
  alertsCount?: number;
  systemStatus?: {
    status?: string;
    uptime?: number;
    memory?: number;
    cpu?: number;
  }
}
import {
  Building2,
  CreditCard,
  Shield,
  Mail,
  Tag,
  Megaphone,
  BarChart2,
  FileText,
  MessageSquare,
  ShieldAlert,
  Home,
  Settings,
  AlertTriangle,
  Menu,
  X,
  LogOut,
  DollarSign,
} from 'lucide-react';
import { Layout, LayoutHeader, LayoutContent, LayoutSidebar } from '@/components/layout';
import { Logo } from '@/components/logo';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/use-auth';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface SuperAdminLayoutProps {
  children: ReactNode;
}

interface NavItem {
  title: string;
  href: string;
  icon: React.ReactNode;
  variant?: 'default' | 'ghost' | 'link' | 'destructive' | 'outline' | 'secondary';
  badge?: number | string | null | undefined;
}

const mainNavItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/admin',
    icon: <Home className="h-4 w-4" />,
  },
  {
    title: 'Tenant Management',
    href: '/admin/tenants',
    icon: <Building2 className="h-4 w-4" />,
  },
  {
    title: 'Billing',
    href: '/admin/billing',
    icon: <CreditCard className="h-4 w-4" />,
  },
  {
    title: 'Email Provider',
    href: '/admin/email',
    icon: <Mail className="h-4 w-4" />,
  },
  {
    title: 'Email Templates',
    href: '/admin/email/templates',
    icon: <FileText className="h-4 w-4" />,
  },
  {
    title: 'Product Plans',
    href: '/admin/plans',
    icon: <Tag className="h-4 w-4" />,
  },
  {
    title: 'Analytics',
    href: '/admin/analytics',
    icon: <BarChart2 className="h-4 w-4" />,
  },
  {
    title: 'Blog Management',
    href: '/admin/blog',
    icon: <FileText className="h-4 w-4" />,
  },
  {
    title: 'Case Studies',
    href: '/admin/case-studies',
    icon: <FileText className="h-4 w-4" />,
  },
  {
    title: 'Contact Forms',
    href: '/admin/contact',
    icon: <MessageSquare className="h-4 w-4" />,
  },
  {
    title: 'Import History',
    href: '/admin/import-history',
    icon: <FileText className="h-4 w-4" />,
  },
  {
    title: 'Coupons',
    href: '/admin/coupons',
    icon: <Tag className="h-4 w-4" />,
  },
  {
    title: 'Invoices',
    href: '/admin/invoices',
    icon: <DollarSign className="h-4 w-4" />,
  },
  {
    title: 'Settings',
    href: '/admin/settings',
    icon: <Settings className="h-4 w-4" />,
  },
];

// Sidebar Content Component for reuse between desktop and mobile views
function SidebarContent({ navItems, location, handleLogout, user }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-[60px] items-center border-b px-6">
        <Logo className="h-6 w-auto" />
        <div className="ml-auto text-xs font-semibold text-rose-600">Admin</div>
      </div>
      <ScrollArea className="flex-1 px-2">
        <div className="p-2">
          <h2 className="mb-2 px-4 text-xs font-semibold tracking-tight">Admin Console</h2>
          <nav className="space-y-1">
            {navItems.map((item) => (
              <Button
                key={item.href}
                variant={
                  location === item.href
                    ? 'default'
                    : (item.variant || 'ghost')
                }
                size="sm"
                className="w-full justify-start"
                asChild
              >
                <Link href={item.href}>
                  {item.icon}
                  <span className="ml-2">{item.title}</span>
                  {item.badge && (
                    <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                      {item.badge}
                    </span>
                  )}
                </Link>
              </Button>
            ))}
          </nav>
        </div>
      </ScrollArea>
      <div className="mt-auto p-4">
        <Separator className="mb-4" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>
                      {user?.username?.substring(0, 2).toUpperCase() || 'AD'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start text-sm">
                    <span className="font-medium">{user?.username || 'Admin'}</span>
                    <span className="text-xs text-muted-foreground">Super Admin</span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link href="/admin/settings">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
}

// Mobile Sidebar Component
function SuperAdminMobileSidebar({ navItems, location, handleLogout, user }) {
  const [open, setOpen] = useState(false);
  
  return (
    <div className="md:hidden">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="text-slate-500">
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle admin menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[280px] p-0">
          <SidebarContent 
            navItems={navItems} 
            location={location} 
            handleLogout={handleLogout}
            user={user}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}

// Mobile Header Component with Profile and Logout
function SuperAdminMobileHeader({ user, handleLogout }) {
  return (
    <div className="flex items-center justify-between px-4 h-14 border-b md:hidden">
      <div className="font-bold text-xl flex items-center">
        <Logo className="h-5 w-auto mr-2" />
        <span className="ml-2 text-xs text-rose-600 font-semibold">Admin</span>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="rounded-full p-0 w-8 h-8">
            <Avatar className="h-8 w-8">
              <AvatarFallback>
                {user?.username?.substring(0, 2).toUpperCase() || 'AD'}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link href="/admin/settings">
              <Settings className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>Logout</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

const SuperAdminLayout: React.FC<SuperAdminLayoutProps> = ({ children }) => {
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();
  const [navItems, setNavItems] = useState(mainNavItems);
  const isMobile = useMobile();
  
  // Apply admin-mode CSS and suppress external chat widgets
  useEffect(() => {
    document.body.classList.add('admin-mode');
    document.documentElement.classList.add('admin-mode');

    const style = document.createElement('style');
    style.id = 'hide-external-chat-admin';
    style.textContent = `
      chat-widget, chat-widget.hydrated,
      [data-loader-instance*="leadconnector"],
      [src*="leadconnectorhq.com"],
      .woot-widget-holder, .woot--bubble-holder,
      #crisp-chatbox, .crisp-client,
      #intercom-container, .intercom-launcher {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.body.classList.remove('admin-mode');
      document.documentElement.classList.remove('admin-mode');
      document.getElementById('hide-external-chat-admin')?.remove();
    };
  }, []);
  
  // Fetch admin dashboard stats to get alerts count
  const { data: adminStats, isLoading } = useQuery<AdminStats>({
    queryKey: ['/api/admin/system/dashboard-stats'],
  });

  // Fetch feature flags to dynamically show/hide menu items
  const { data: featureFlags } = useQuery({
    queryKey: ['/api/admin/settings/features'],
    queryFn: async () => {
      try {
        const response = await apiRequest<{ emailTemplatesEnabled: boolean; automationWorkflowsEnabled: boolean }>('/api/admin/settings/features', { method: 'GET' });
        return response;
      } catch {
        return { emailTemplatesEnabled: true, automationWorkflowsEnabled: true };
      }
    }
  });

  // Update nav items with dynamic data when stats or feature flags are loaded
  useEffect(() => {
    let updatedNavItems = [...mainNavItems];
    
    // Dynamic feature visibility
    if (featureFlags) {
      if (featureFlags.emailTemplatesEnabled === false) {
        updatedNavItems = updatedNavItems.filter(item => item.href !== '/admin/email/templates');
      }
    }
    
    setNavItems(updatedNavItems);
  }, [adminStats, featureFlags]);

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  return (
    <Layout>
      {/* Desktop sidebar - hidden on mobile */}
      {!isMobile && (
        <LayoutSidebar>
          <SidebarContent 
            navItems={navItems} 
            location={location} 
            handleLogout={handleLogout}
            user={user}
          />
        </LayoutSidebar>
      )}
      
      <div className="flex flex-col flex-1 min-h-0">
        {/* Mobile header with hamburger menu and profile dropdown */}
        {isMobile && (
          <header className="bg-white border-b border-slate-200 flex-shrink-0">
            <div className="flex items-center h-14 px-4">
              <SuperAdminMobileSidebar 
                navItems={navItems}
                location={location}
                handleLogout={handleLogout}
                user={user}
              />
              <div className="ml-3 font-bold text-primary">SaaSGama Admin</div>
              <div className="ml-auto">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="rounded-full p-0 w-8 h-8">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>
                          {user?.username?.substring(0, 2).toUpperCase() || 'AD'}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link href="/admin/settings">
                        <Settings className="mr-2 h-4 w-4" />
                        <span>Settings</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Logout</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>
        )}
        
        {/* Main content area with proper scrolling */}
        <div className="flex-1 overflow-y-auto">
          <LayoutContent>{children}</LayoutContent>
        </div>
      </div>
    </Layout>
  );
};

export default SuperAdminLayout;